import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { legalDocumentsApi } from '@/lib/pillarApi';
import type { LegalDocumentAcceptanceListItem } from '@pillar-portal/api-types';
import {
    LegalDocumentAcceptanceFilterControls,
    legalDocumentAcceptanceFilterRequest,
    type LegalDocumentAcceptanceFilters,
} from './LegalDocumentAcceptanceFilterControls';
import { SYSTEM_DISPLAY_TIME_ZONE, formatTimestampForMerchantUi } from '@pillar-portal/api-types';
import { buildLegalDocumentIframeSrcDoc } from './legal-document-rendering';
import { LegalCsvExportButton } from './LegalCsvExportButton';
import { collectAllPages, toCsv } from './legal-csv-export';

function AcceptedText({ acceptance }: { acceptance: LegalDocumentAcceptanceListItem }) {
    const { data, isLoading, isError } = useQuery({
        queryKey: [
            'legalDocumentAcceptance',
            acceptance.acceptance_kind,
            acceptance.acceptance_uuid,
        ],
        queryFn: async () =>
            await legalDocumentsApi.getLegalDocumentAcceptance(
                acceptance.acceptance_kind,
                acceptance.acceptance_uuid
            ),
        staleTime: Infinity,
    });
    if (isLoading) return <p>Loading the accepted text...</p>;
    if (isError || data === undefined) {
        return <p role="alert">The accepted text could not be loaded.</p>;
    }
    return (
        <iframe
            title="Accepted text"
            sandbox=""
            className="mt-2 h-[50vh] w-full rounded border"
            srcDoc={buildLegalDocumentIframeSrcDoc({
                legalDocumentContentType: data.accepted_content.legal_document_content_type,
                legalDocumentText: data.accepted_content.legal_document_text,
            })}
        />
    );
}

async function buildAcceptancesCsv(
    legalDocumentUuid: string,
    filters: LegalDocumentAcceptanceFilters
): Promise<{ csv: string; truncated: boolean }> {
    const { rows, truncated } = await collectAllPages(async (page) => {
        const response = await legalDocumentsApi.searchLegalDocumentAcceptancesPage(
            legalDocumentUuid,
            { ...page, ...legalDocumentAcceptanceFilterRequest(filters) }
        );
        return { items: response.acceptances, pagination: response.pagination };
    });
    const csv = toCsv(
        [
            'Accepted at (UTC)',
            'Email',
            'Organization',
            'Kind',
            'Signing authority confirmed',
            'Accepted the current text',
            'Withdrawn at (UTC)',
            'Withdrawn by',
        ],
        rows.map((acceptance) => [
            acceptance.accepted_ts,
            acceptance.user.primary_email,
            acceptance.organization?.organization_name ?? null,
            acceptance.acceptance_kind === 'organization' ? 'Signature' : 'Personal acceptance',
            acceptance.acceptance_kind === 'organization'
                ? acceptance.signer_authority_confirmed
                : null,
            acceptance.accepted_text_matches_current,
            acceptance.withdrawal?.withdrawn_ts ?? null,
            acceptance.withdrawal?.withdrawn_by.primary_email ?? null,
        ])
    );
    return { csv, truncated };
}

/**
 * Soft-deletes one acceptance after confirmation. The acceptance stays on
 * record, and the User or PRINCIPAL is asked to accept again.
 */
function WithdrawAcceptanceAction({
    acceptance,
    legalDocumentUuid,
}: {
    acceptance: LegalDocumentAcceptanceListItem;
    legalDocumentUuid: string;
}) {
    const queryClient = useQueryClient();
    const [confirming, setConfirming] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const mutation = useMutation({
        mutationFn: async () =>
            await legalDocumentsApi.withdrawLegalDocumentAcceptance(
                acceptance.acceptance_kind,
                acceptance.acceptance_uuid
            ),
        onSuccess: async () => {
            setConfirming(false);
            setErrorMessage(null);
            await queryClient.invalidateQueries({
                queryKey: ['legalDocumentAcceptances', legalDocumentUuid],
            });
        },
        onError: (error: unknown) => {
            setErrorMessage(
                error instanceof Error ? error.message : 'Unable to withdraw the acceptance.'
            );
        },
    });
    if (!confirming) {
        return (
            <Button variant="outline" className="mt-2 ml-2" onClick={() => setConfirming(true)}>
                Withdraw
            </Button>
        );
    }
    return (
        <div className="mt-2 space-y-2 rounded border border-amber-200 bg-amber-50 p-3">
            <p>
                {acceptance.acceptance_kind === 'organization'
                    ? 'Withdraw this signature? It stays on record, but the Organization must be signed for again.'
                    : 'Withdraw this acceptance? It stays on record, but the User must accept the document again.'}
            </p>
            {errorMessage === null ? null : (
                <p role="alert" className="text-red-700">
                    {errorMessage}
                </p>
            )}
            <div className="flex gap-2">
                <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
                    {mutation.isPending ? 'Withdrawing...' : 'Confirm withdrawal'}
                </Button>
                <Button
                    variant="outline"
                    disabled={mutation.isPending}
                    onClick={() => {
                        setConfirming(false);
                        setErrorMessage(null);
                    }}
                >
                    Keep
                </Button>
            </div>
        </div>
    );
}

/** Who accepted a legal document, and the exact text each of them accepted. */
export function LegalDocumentAcceptancesList({ legalDocumentUuid }: { legalDocumentUuid: string }) {
    const [openAcceptanceUuid, setOpenAcceptanceUuid] = useState<string | null>(null);
    const [filters, setFilters] = useState<LegalDocumentAcceptanceFilters>({
        organization: null,
        acceptedText: null,
    });
    const queryKeyPrefix = useMemo(
        () =>
            [
                'legalDocumentAcceptances',
                legalDocumentUuid,
                filters.organization?.organization_uuid ?? 'any-organization',
                filters.acceptedText ?? 'any-text',
            ] as const,
        [legalDocumentUuid, filters]
    );
    return (
        <InfiniteScrollList<LegalDocumentAcceptanceListItem, string, typeof queryKeyPrefix>
            pageTitle="Acceptances"
            cardTitle="Acceptances"
            queryPlaceholder="Search by email or Organization"
            queryKeyPrefix={queryKeyPrefix}
            pageSize={25}
            showPageHeader={false}
            controlsPlacement="above-search"
            cardHeaderActions={
                <LegalCsvExportButton
                    filename={`legal-document-${legalDocumentUuid}-acceptances.csv`}
                    buildCsv={async () => await buildAcceptancesCsv(legalDocumentUuid, filters)}
                />
            }
            controls={
                <LegalDocumentAcceptanceFilterControls filters={filters} onChange={setFilters} />
            }
            fetchPage={async (params) => {
                const response = await legalDocumentsApi.searchLegalDocumentAcceptancesPage(
                    legalDocumentUuid,
                    {
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        ...legalDocumentAcceptanceFilterRequest(filters),
                    }
                );
                return { items: response.acceptances, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) =>
                pageParam === null || pageParam === undefined
                    ? undefined
                    : z.string().parse(pageParam)
            }
            stateCopy={{
                loadingLabel: 'Loading acceptances...',
                emptyLabel: 'No acceptances match.',
                endOfListLabel: 'End of acceptances.',
                genericErrorLabel: 'Unable to load acceptances.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((acceptance) => (
                        <div
                            key={acceptance.acceptance_uuid}
                            className="rounded border p-3 text-sm"
                        >
                            <div className="font-medium">
                                {acceptance.user.primary_email}
                                {acceptance.organization === null
                                    ? ''
                                    : ` · ${acceptance.organization.organization_name}`}
                            </div>
                            <div>
                                {acceptance.acceptance_kind === 'organization'
                                    ? 'Signed for the Organization'
                                    : 'Accepted personally'}{' '}
                                on{' '}
                                {formatTimestampForMerchantUi(
                                    acceptance.accepted_ts,
                                    SYSTEM_DISPLAY_TIME_ZONE
                                )}
                                {acceptance.acceptance_kind === 'organization'
                                    ? acceptance.signer_authority_confirmed
                                        ? ' · signing authority confirmed'
                                        : ' · signing authority not recorded'
                                    : ''}
                            </div>
                            <div className="text-gray-600">
                                {acceptance.accepted_text_matches_current
                                    ? 'Accepted the current text.'
                                    : 'Accepted an earlier text of this document.'}
                            </div>
                            {acceptance.withdrawal === null ? null : (
                                <div className="font-medium text-amber-800">
                                    Withdrawn on{' '}
                                    {formatTimestampForMerchantUi(
                                        acceptance.withdrawal.withdrawn_ts,
                                        SYSTEM_DISPLAY_TIME_ZONE
                                    )}{' '}
                                    by {acceptance.withdrawal.withdrawn_by.primary_email}; it no
                                    longer counts.
                                </div>
                            )}
                            <Button
                                variant="outline"
                                className="mt-2"
                                onClick={() =>
                                    setOpenAcceptanceUuid(
                                        openAcceptanceUuid === acceptance.acceptance_uuid
                                            ? null
                                            : acceptance.acceptance_uuid
                                    )
                                }
                            >
                                {openAcceptanceUuid === acceptance.acceptance_uuid
                                    ? 'Hide accepted text'
                                    : 'View accepted text'}
                            </Button>
                            {acceptance.withdrawal === null ? (
                                <WithdrawAcceptanceAction
                                    acceptance={acceptance}
                                    legalDocumentUuid={legalDocumentUuid}
                                />
                            ) : null}
                            {openAcceptanceUuid === acceptance.acceptance_uuid ? (
                                <AcceptedText acceptance={acceptance} />
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        />
    );
}
